import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Source = 'banner_dismiss' | 'profile_toggle';

interface State {
  loading: boolean;
  optedIn: boolean;
  email: string | null;
  userId: string | null;
}

/**
 * Manages the current user's opt-in to the launch announcement list
 * (`beta_testers` table). Server-side RLS guarantees they can only
 * opt themselves in, with their own auth email, and only once.
 */
export function useBetaTester() {
  const [state, setState] = useState<State>({
    loading: true,
    optedIn: false,
    email: null,
    userId: null,
  });

  const refresh = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user ?? null;
    if (!user) {
      setState({ loading: false, optedIn: false, email: null, userId: null });
      return;
    }
    const { data } = await supabase
      .from('beta_testers')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();
    setState({
      loading: false,
      optedIn: !!data,
      email: user.email ?? null,
      userId: user.id,
    });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const optIn = useCallback(
    async (source: Source): Promise<{ ok: boolean; error?: string }> => {
      if (!state.userId || !state.email) {
        return { ok: false, error: 'Please sign in first.' };
      }
      const { error } = await supabase.from('beta_testers').insert({
        user_id: state.userId,
        email: state.email.trim().toLowerCase(),
        source,
      });
      if (error && (error as { code?: string }).code !== '23505') {
        return { ok: false, error: error.message };
      }
      setState(s => ({ ...s, optedIn: true }));
      return { ok: true };
    },
    [state.userId, state.email],
  );

  const optOut = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!state.userId) return { ok: false, error: 'Please sign in first.' };
    const { error } = await supabase
      .from('beta_testers')
      .delete()
      .eq('user_id', state.userId);
    if (error) return { ok: false, error: error.message };
    setState(s => ({ ...s, optedIn: false }));
    return { ok: true };
  }, [state.userId]);

  return { ...state, refresh, optIn, optOut };
}
